import { v4 as uuidv4 } from "uuid";

import {
  MINI_NODE_INITIAL_SPACING,
  NODE_WIDTH,
} from "src/pages/journeys/constants";
import type { ExecutableJourneyNode } from "src/pages/journeys/types";
import { JourneyNodeType } from "src/types/journeys";
import { ConditionType } from "src/types/visual";

/**
 * Create initial branch nodes for a given node type
 */
export const getInitialBranchNodes = (
  type: JourneyNodeType,
  parentNodePosition: { x: number; y: number },
): ExecutableJourneyNode[] => {
  const id1 = uuidv4();
  const id2 = uuidv4();

  switch (type) {
    case JourneyNodeType.Splits:
      return [
        {
          id: id1,
          type: JourneyNodeType.SplitBranch,
          data: {
            id: id1,
            name: "Group 1",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.SplitBranch,
              percentage: 50,
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
        {
          id: id2,
          type: JourneyNodeType.SplitBranch,
          data: {
            id: id2,
            name: "Group 2",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.SplitBranch,
              percentage: 50,
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2 + NODE_WIDTH,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
      ];
    case JourneyNodeType.Segments:
      return [
        {
          id: id1,
          type: JourneyNodeType.SegmentBranch,
          data: {
            id: id1,
            name: "Everyone else",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.SegmentBranch,
              segment_priority_rank: 0,
              segment_is_catch_all: true,
              segment_conditions: {
                type: ConditionType.And,
                conditions: [],
                id: crypto.randomUUID(),
              },
            },
          },
          position: {
            x: parentNodePosition.x,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
      ];
    case JourneyNodeType.Function:
      return [
        {
          id: id1,
          type: JourneyNodeType.SegmentBranch,
          data: {
            id: id1,
            name: "Success",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.SegmentBranch,
              branch_method: "event",
              segment_priority_rank: 0,
              segment_is_catch_all: false,
              segment_conditions: {
                type: ConditionType.And,
                conditions: [],
                id: crypto.randomUUID(),
              },
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
        {
          id: id2,
          type: JourneyNodeType.SegmentBranch,
          data: {
            id: id2,
            name: "Everyone else",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.SegmentBranch,
              segment_priority_rank: 1,
              segment_is_catch_all: true,
              segment_conditions: {
                type: ConditionType.And,
                conditions: [],
                id: crypto.randomUUID(),
              },
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2 + NODE_WIDTH,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
      ];
    case JourneyNodeType.WaitUntilEvent:
      return [
        {
          id: id1,
          type: JourneyNodeType.WaitUntilEventBranch,
          data: {
            id: id1,
            name: "Rule met",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.WaitUntilEventBranch,
              branch: "event",
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
        {
          id: id2,
          type: JourneyNodeType.WaitUntilEventBranch,
          data: {
            id: id2,
            name: "Rule not met",
            description: null,
            segment_id: null,
            event_relationship_id: null,
            number_users: null,
            number_unique_users: null,
            sync_configs: null,
            message_config: null,
            config: {
              type: JourneyNodeType.WaitUntilEventBranch,
              branch: "timeout",
            },
          },
          position: {
            x: parentNodePosition.x - NODE_WIDTH / 2 + NODE_WIDTH,
            y: parentNodePosition.y + MINI_NODE_INITIAL_SPACING,
          },
        },
      ];
    default:
      return [];
  }
};
